// Claim refs in a chat's answer ([c5], [c5, c6]) as links to what they rest
// on, on the workspace in front: the board picks the claim out of its
// evidence and lists the claims in this answer's order. Only a ref naming a
// claim that board can show is linked; the rest stay as written.

import { Fragment } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import { claimRefsIn } from "../lib/answerEvidence.js";
import { askClaimEvidence } from "../lib/workspaceStore.js";

const REF_CLASS =
    "cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[length:inherit] " +
    "leading-[inherit] text-[color:inherit] underline decoration-dotted " +
    "decoration-[color:var(--line-strong)] underline-offset-[2px] " +
    "hover:text-[color:var(--green)] hover:decoration-solid " +
    "hover:decoration-[color:var(--green)]";

/**
 * A `linkifyText` for one answer (`answer`, its whole text): its refs to the
 * claims in `shown` become links, and every other stretch of a run goes on to
 * `next`. With nothing to link, `next` itself.
 */
export function makeClaimRefLinkifier(answer, shown, next = null) {
    if (!answer || !shown?.size || !claimRefsIn(answer).some((r) => r.ids.some((id) => shown.has(id)))) return next;
    return function linkifyText({ text, key }) {
        const refs = claimRefsIn(text).filter((r) => r.ids.some((id) => shown.has(id)));
        if (!refs.length) return next ? next({ text, key }) : null;
        const out = [];
        const rest = (s, k) => {
            if (!s) return;
            const r = next ? next({ text: s, key: k }) : null;
            out.push(r == null ? s : r);
        };
        let cursor = 0;
        refs.forEach((r, i) => {
            rest(text.slice(cursor, r.start), `${key}:t${i}`);
            out.push(
                <HoverTooltip key={`${key}:c${i}`} placement="top" label="Show what it rests on, on the investigation">
                    <button
                        type="button"
                        className={REF_CLASS}
                        onClick={() => askClaimEvidence(answer, r.ids)}
                        aria-label={`Show what ${r.ids.join(", ")} rests on, on the investigation`}
                        data-claim-ref={r.ids.join(",")}
                    >
                        {text.slice(r.start, r.end)}
                    </button>
                </HoverTooltip>,
            );
            cursor = r.end;
        });
        rest(text.slice(cursor), `${key}:t${refs.length}`);
        return <Fragment key={key}>{out}</Fragment>;
    };
}
